/**
 * Cache busting utility
 * Checks if the deployed version differs from the localStorage version
 * and prompts user to refresh if needed
 */

export interface MetaInfo {
  version: string;
}

/**
 * Fetches the deployed meta.json file
 */
export async function fetchDeployedMeta(): Promise<MetaInfo | null> {
  try {
    const response = await fetch("/meta.json?" + new Date().getTime(), {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      console.warn("Failed to fetch meta.json:", response.statusText);
      return null;
    }

    const meta: MetaInfo = await response.json();
    return meta;
  } catch (error) {
    console.warn("Error fetching meta.json:", error);
    return null;
  }
}

/**
 * Gets the version from localStorage
 */
export function getLocalStorageVersion(): string | null {
  try {
    const stored = localStorage.getItem("app-version");
    return stored;
  } catch (error) {
    console.warn("Error reading localStorage version:", error);
    return null;
  }
}

/**
 * Saves version to localStorage
 */
export function saveLocalStorageVersion(version: string): void {
  try {
    localStorage.setItem("app-version", version);
  } catch (error) {
    console.error("Error saving localStorage version:", error);
  }
}

/**
 * Checks if a new version is available
 */
export function isNewVersionAvailable(
  localStorageVersion: string | null,
  deployedVersion: string | null,
): boolean {
  if (!localStorageVersion || !deployedVersion) return false;

  // Compare versions (timestamps)
  return localStorageVersion !== deployedVersion;
}

/**
 * Clears cache and reloads the page
 */
export function clearCacheAndReload(): void {
  // Clear all caches
  if ("caches" in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }

  // Hard reload
  window.location.reload();
}
